import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Calendar, X, Loader2, Video, ExternalLink, Webhook, Key, RefreshCw, Copy, MoreHorizontal, Scissors } from "lucide-react";
import { Link as RouterLink } from 'react-router-dom';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ApiRequestCardProps {
  id: string;
  name: string;
  dateLabel: string;
  processStatus: string;
  statusLabel: string;
  statusVariant: string;
  statusIcon: React.ElementType;
  thumbnail?: string;
  baseVideoUrl?: string;
  webhookUrl?: string | null;
  apiKeyName?: string | null;
  clipsCount: number;
  onRetriggerWebhook?: (id: string) => void;
  isRetriggering?: boolean;
}

export const ApiRequestCard = ({
  id,
  name,
  dateLabel,
  processStatus,
  statusLabel,
  statusVariant,
  statusIcon: StatusIcon,
  thumbnail,
  baseVideoUrl,
  webhookUrl,
  apiKeyName,
  clipsCount,
  onRetriggerWebhook,
  isRetriggering,
}: ApiRequestCardProps) => {
  const { t } = useTranslation();
  const isProcessing = processStatus === 'processing' || processStatus === 'pending';
  const isExpired = processStatus === 'expired';
  const canRetrigger = !!webhookUrl && (processStatus === 'completed' || processStatus === 'failed');

  return (
    <Card className="relative overflow-hidden hover:border-primary/30 transition-all duration-300 group">
      {/* Thumbnail */}
      <RouterLink to={`/project/${id}/clips`} className="block">
        <div className="aspect-video bg-secondary overflow-hidden relative">
          {!isExpired && thumbnail ? (
            <img
              src={thumbnail}
              alt={name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full bg-secondary flex items-center justify-center">
              <Scissors className="w-6 h-6 text-muted-foreground/40" />
            </div>
          )}

          {/* Status badge overlay */}
          <div className="absolute top-2 right-2">
            <Badge
              variant={statusVariant as any}
              className="text-[10px] flex items-center gap-1 backdrop-blur-sm bg-opacity-90 shadow-sm"
            >
              <StatusIcon className="w-2.5 h-2.5" />
              {statusLabel}
            </Badge>
          </div>

          {/* Clips count pill */}
          <div className="absolute bottom-2 left-2">
            <span className="inline-flex items-center gap-1 text-[10px] text-white bg-black/60 backdrop-blur-sm px-1.5 py-0.5 rounded">
              <Video className="w-2.5 h-2.5" />
              {clipsCount}
            </span>
          </div>

          {/* Processing overlay */}
          {isProcessing && (
            <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center">
              <div className="flex flex-col items-center gap-1.5">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                <span className="text-[10px] text-white/80 font-medium">{statusLabel}</span>
              </div>
            </div>
          )}

          {/* Expired overlay */}
          {isExpired && (
            <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
              <div className="text-center">
                <X className="w-6 h-6 text-amber-500 mx-auto mb-1" />
                <span className="text-amber-500 text-[10px] font-medium">{statusLabel}</span>
              </div>
            </div>
          )}
        </div>
      </RouterLink>

      {/* Content */}
      <div className="p-3 space-y-2">
        <RouterLink to={`/project/${id}/clips`} className="block">
          <h3 className="font-medium text-sm leading-tight line-clamp-1 group-hover:text-primary transition-colors">
            {name}
          </h3>
        </RouterLink>

        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {dateLabel}
          </span>
          {apiKeyName && (
            <span className="inline-flex items-center gap-1 truncate">
              <Key className="w-3 h-3 shrink-0" />
              <span className="truncate">{apiKeyName}</span>
            </span>
          )}
        </div>

        {/* Footer row: source link + webhook actions */}
        <div className="flex items-center justify-between pt-1 border-t border-border/50">
          <div className="flex items-center gap-2">
            {baseVideoUrl && (
              <a
                href={baseVideoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="w-3 h-3" />
                {t('apiRequests.card.source')}
              </a>
            )}
          </div>

          {webhookUrl && (
            <div className="flex items-center gap-1">
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground max-w-[100px] truncate">
                <Webhook className="w-3 h-3 shrink-0" />
                <span className="truncate">{new URL(webhookUrl).hostname}</span>
              </span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 shrink-0 text-muted-foreground hover:text-foreground"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="w-3.5 h-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(webhookUrl);
                    }}
                  >
                    <Copy className="w-3.5 h-3.5 mr-2" />
                    {t('apiRequests.card.copyWebhookUrl')}
                  </DropdownMenuItem>
                  {canRetrigger && onRetriggerWebhook && (
                    <DropdownMenuItem
                      disabled={isRetriggering}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRetriggerWebhook(id);
                      }}
                    >
                      <RefreshCw className={`w-3.5 h-3.5 mr-2 ${isRetriggering ? 'animate-spin' : ''}`} />
                      {t('apiRequests.card.retriggerWebhook')}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
